import * as React from "react"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { DayPicker } from "react-day-picker"

import { cn } from "@/lib/utils"
import { buttonVariants } from "@/components/ui/button"

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}) {
  // `captionLayout="dropdown-buttons"` (or "dropdown") shows Month + Year as
  // Select dropdowns inside the caption. That mode needs different spacing
  // than the default single-text caption — otherwise the nav chevrons stack
  // on top of the dropdowns. We detect the mode up-front and swap in a
  // dropdown-friendly caption layout.
  const isDropdownCaption =
    props.captionLayout === "dropdown-buttons" ||
    props.captionLayout === "dropdown";

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: cn(
          "pt-1 items-center",
          isDropdownCaption
            ? "flex justify-between gap-2 px-1"
            : "flex justify-center relative"
        ),
        caption_label: cn(
          "text-sm font-medium",
          // In dropdown mode the visible label is replaced by the dropdowns.
          isDropdownCaption && "sr-only"
        ),
        caption_dropdowns: "flex items-center gap-1.5 flex-1 justify-center",
        dropdown: cn(
          "h-8 rounded-md border border-slate-300 bg-white px-2 pr-7 text-sm",
          "focus:outline-none focus:ring-2 focus:ring-slate-300"
        ),
        dropdown_month: "relative",
        dropdown_year: "relative",
        vhidden: "sr-only",
        nav: "flex items-center gap-1",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-70 hover:opacity-100"
        ),
        // In default mode keep the original absolute placement so the chevrons
        // flank the centered label. In dropdown mode use inline placement so
        // they live on either side of the dropdown row.
        nav_button_previous: isDropdownCaption ? "order-first" : "absolute left-1",
        nav_button_next: isDropdownCaption ? "order-last" : "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell:
          "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative p-0 text-center text-sm focus-within:relative focus-within:z-20 [&:has([aria-selected])]:bg-accent [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected].day-range-end)]:rounded-r-md",
          props.mode === "range"
            ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md"
            : "[&:has([aria-selected])]:rounded-md"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-8 w-8 p-0 font-normal aria-selected:opacity-100"
        ),
        day_range_start: "day-range-start",
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground aria-selected:bg-accent/50 aria-selected:text-muted-foreground",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle:
          "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ className, ...props }) => (
          <ChevronLeft className={cn("h-4 w-4", className)} {...props} />
        ),
        IconRight: ({ className, ...props }) => (
          <ChevronRight className={cn("h-4 w-4", className)} {...props} />
        ),
      }}
      {...props} />
  );
}
Calendar.displayName = "Calendar"

export { Calendar }
